import { useState, useEffect } from 'react';
import { X, Trash2 } from 'lucide-react';
import { supabase, AudioPin } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

type EditPinModalProps = {
  pin: AudioPin;
  onClose: () => void;
  onSuccess: () => void;
};

export function EditPinModal({ pin, onClose, onSuccess }: EditPinModalProps) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [title, setTitle] = useState(pin.title);
  const [description, setDescription] = useState(pin.description || '');
  const [youtubeUrl, setYoutubeUrl] = useState(pin.youtube_url);
  const [locationName, setLocationName] = useState(pin.location_name || '');
  const [tags, setTags] = useState(pin.tags?.join(', ') || '');
  const [isPublic, setIsPublic] = useState(pin.is_public);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Update form fields when pin prop changes
  useEffect(() => {
    setTitle(pin.title);
    setDescription(pin.description || '');
    setYoutubeUrl(pin.youtube_url);
    setLocationName(pin.location_name || '');
    setTags(pin.tags?.join(', ') || '');
    setIsPublic(pin.is_public);
    setError('');
    setShowDeleteConfirm(false);
  }, [pin.id]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showDeleteConfirm) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, showDeleteConfirm]);

  const extractVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!user) {
        throw new Error('You must be logged in to edit a pin');
      }

      if (pin.user_id !== user.id) {
        throw new Error('You can only edit your own pins');
      }

      const videoId = extractVideoId(youtubeUrl);
      if (!videoId) {
        throw new Error('Invalid YouTube URL');
      }

      const { error: updateError } = await supabase
        .from('audio_pins')
        .update({
          title: title.trim(),
          description: description.trim() || null,
          youtube_url: youtubeUrl.trim(),
          youtube_video_id: videoId,
          location_name: locationName.trim() || null,
          tags: tags.split(',').map(tag => tag.trim()).filter(Boolean),
          is_public: isPublic,
          updated_at: new Date().toISOString(),
        })
        .eq('id', pin.id);

      if (updateError) throw updateError;

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update audio pin');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setError('');
    setLoading(true);

    try {
      if (!user) {
        throw new Error('You must be logged in to delete a pin');
      }

      if (pin.user_id !== user.id) {
        throw new Error('You can only delete your own pins');
      }

      const { error: deleteError } = await supabase
        .from('audio_pins')
        .delete()
        .eq('id', pin.id);

      if (deleteError) throw deleteError;

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete audio pin');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">{t('editPin.title')}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-full text-gray-300 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-1">
                {t('createPin.pinTitle')} *
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                required
                maxLength={200}
              />
            </div>

            <div>
              <label htmlFor="youtubeUrl" className="block text-sm font-medium text-gray-300 mb-1">
                {t('createPin.youtubeUrl')} *
              </label>
              <input
                id="youtubeUrl"
                type="url"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder={t('createPin.youtubeUrlPlaceholder')}
                className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-1">
                {t('createPin.description')}
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                maxLength={500}
              />
            </div>

            <div>
              <label htmlFor="locationName" className="block text-sm font-medium text-gray-300 mb-1">
                {t('createPin.location')}
              </label>
              <input
                id="locationName"
                type="text"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                placeholder="e.g., Central Park, NYC"
                className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-300 mb-1">
                {t('createPin.tags')}
              </label>
              <input
                id="tags"
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder={t('createPin.tagsPlaceholder')}
                className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
              />
            </div>

            <div className="flex items-center">
              <input
                id="isPublic"
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="w-4 h-4 text-[#00c7b7] border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="isPublic" className="ml-2 text-sm text-gray-300">
                Make this pin public
              </label>
            </div>

            <div className="text-sm text-gray-300 bg-gray-900 p-3 rounded-lg">
              <p>Location: {pin.latitude.toFixed(6)}, {pin.longitude.toFixed(6)}</p>
            </div>

            {error && (
              <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="flex flex-col gap-3 pt-4">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-gray-600 text-gray-300 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                >
                  {t('createPin.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-[#00c7b7] hover:bg-[#00c7b7] text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t('editPin.updating') : t('editPin.update')}
                </button>
              </div>

              {!showDeleteConfirm ? (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-red-50 text-red-600 border border-red-200 rounded-lg font-semibold hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('editPin.delete') || 'Delete Pin'}
                </button>
              ) : (
                <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                  <p className="text-sm text-red-800 mb-3 font-medium">
                    {t('editPin.deleteConfirm') || 'Are you sure you want to delete this pin? This action cannot be undone.'}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={loading}
                      className="flex-1 px-4 py-2 bg-white border border-gray-600 text-gray-300 rounded-lg font-semibold hover:bg-gray-700 transition-colors text-sm"
                    >
                      {t('createPin.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={loading}
                      className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {loading ? (t('editPin.deleting') || 'Deleting...') : (t('editPin.confirmDelete') || 'Yes, Delete')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
